'use strict';

const FOXSELL_EVENTS = {
  bundleUpdated: 'foxsell:bundle-updated',
};

/**
 * @returns {FoxSellBundle}
 */
const emptyBundleState = {
  items: [],
  addOns: {
    addOnStrategy: '',
    enabled: false,
    minimum: 0,
    maximum: 0,
    selectedQuantity: 0,
    isMaxedOut: false,
    allowedIds: [],
    items: [],
  },
  isValid: false,
  isAddOnsValid: false,
  isItemsValid: false,
  originalTotalPrice: 0,
  totalPrice: 0,
  totalDiscount: 0,
  id: '',
  qaoEnabled: false,
  priceStrategy: null,
};

/**
 * @type {FoxSellTotalPrice}
 */
const emptyTotalPrice = {
  originalTotalPrice: 0,
  totalPrice: 0,
  totalDiscount: 0,
  discountValue: 0,
  priceStrategy: 'dynamic_pricing',
  itemsTotalPrice: 0,
  addOnsTotalPrice: 0,
};

/**
 * @returns {FoxSellAddOnsConfig}
 */
const emptyAddOnsConfig = {
  addOnStrategy: 'add_on_step',
  allowedIds: [],
  enabled: false,
  minimum: 0,
  maximum: 0,
  selectedQuantity: 0,
  isMaxedOut: false,
};

/**
 * @description Get Item ID from GID
 * @param {string} gid
 * @returns {number}
 */
function getItemIdFromGid(gid) {
  return parseInt(gid.split('/').pop() ?? '0');
}

/**
 * @description Get Add-On price from config
 * @param {number} productId
 * @param {number} variantId
 * @param {FoxSellMixMatchConfig['addOnProductProperties']} addOnProductProperties
 * @returns {number}
 */
function getAddOnPrice(productId, variantId, addOnProductProperties) {
  let pid = `gid://shopify/Product/${productId}`;
  let vid = `gid://shopify/ProductVariant/${variantId}`;
  let addOnProperty = addOnProductProperties[pid]?.variants[vid];
  if(!addOnProperty) return 0;
  return addOnProperty * 100;
}

/**
 * @description List price and compare-at for a variant (Shopify cents).
 * @param {ShopifyVariant[]} variants
 * @param {FoxSellQuantityOption | null | undefined} option
 * @returns {{ price: number; compareAtPrice: number }}
 */
function getVariantPrice(variants, option) {
  if (!option?.variant_id || !option?.price) {
    return { price: 0, compareAtPrice: 0 };
  }
  const variant = variants.find((v) => v.id === parseInt(option.variant_id));
  if (!variant) {
    return { price: 0, compareAtPrice: 0 };
  }
  const price = (option.price.value ?? 0) * 100;
  const compareAtPrice = variant.compare_at_price ?? 0;
  return { price, compareAtPrice };
}

// @ts-check


class FoxSellMixMatch extends HTMLElement {
  constructor() {
    super();
    this.selectedItems = new Map();
    this.selectedAddOns = new Map();
    this.selectedOption = {};
    /** @type {FoxSellMixMatchConfig | null} */
    this.config = null;
    /** @type {FoxSellBundle} */
    this.bundle = emptyBundleState;

    const config = window.foxsell.config[this.dataset.bundleId ?? ''];
    this.config = config ?? null;
  }

  connectedCallback() {
    this.validateBundle();
    // Themes may use this class with foxsell--hidden from legacy step flow; ensure ATC is visible
    this.querySelector('.foxsell-add-to-cart-button')?.classList.remove('foxsell--hidden');
  }

  /**
   * @description Get the current price strategy without mutating config
   * @returns {FoxSellPriceConfig | null}
   */
  getCurrentPriceStrategy() {
    if (!this.config) return null;
    const settingsPrice = this.config.settings.price;
    const qaoEnabled = (this.config.options?.length ?? 0) > 0;
    if (!qaoEnabled) return settingsPrice;

    let value = 0;
    const currentValidOption = this.getCurrentValidOption();
    
    if(currentValidOption) {
      value = currentValidOption.price.value;
    }

    return {
      strategy: settingsPrice.strategy,
      value: value,
    };
  }

  /**
   * @description Get the current valid option based on item count
   * @returns {FoxSellQuantityOption | null}
   */
  getCurrentValidOption() {
    if (!this.config || !this.config.options?.length) return null;

    const items = this.getSelectedItems().flatMap(category =>
      Array.isArray(category.items) ? category.items : []
    );
    const itemsCount = items.reduce((sum, item) => sum + item.quantity, 0);
    return this.config.options.findLast(opt => Number(opt.quantity ?? opt) <= itemsCount) ?? null;
  }

  /**
   * @description Build the bundle state from current selection
   * @param {boolean} isValid
   * @param {boolean} isAddOnsValid
   * @param {boolean} isItemsValid
   * @returns {FoxSellBundle}
   */
  buildBundle(isValid, isAddOnsValid, isItemsValid) {
    if (!this.config) return { ...emptyBundleState };

    const qaoEnabled = (this.config.options?.length ?? 0) > 0;
    const items = this.getSelectedItems();
    const addOnItems = this.getSelectedAddOns();
    const { originalTotalPrice, totalPrice, totalDiscount } = this.getTotalPrice();
    const id = this.config.bundleId ? `${this.config.bundleId}_${Date.now()}` : '';
    const priceStrategy = this.getCurrentPriceStrategy();

    return {
      items,
      addOns: {
        ...this.getAddOnsConfig(),
        items: addOnItems,
      },
      isValid,
      isAddOnsValid,
      isItemsValid,
      originalTotalPrice,
      totalPrice,
      totalDiscount,
      id,
      qaoEnabled,
      priceStrategy,
    };
  }

  /**
   * @param {string} categoryId
   * @returns {FoxSellCategoryConfig | undefined}
   */
  getCategoryConfig(categoryId) {
    if (!this.config) return undefined;
    return this.config.categories?.find((category) => category.id === categoryId);
  }

  /**
   * @param {string} categoryId
   * @param {boolean} createIfNotExists
   * @returns {FoxSellCategory | undefined}
   */
  getCategory(categoryId, createIfNotExists = false) {
    const categoryConfig = this.getCategoryConfig(categoryId);
    if (!categoryConfig) return undefined;

    const category = this.selectedItems.get(categoryId);
    if (!category && createIfNotExists) {
      this.selectedItems.set(categoryId, {
        items: new Map(),
        quantity: 0,
        id: categoryId,
        title: categoryConfig.title,
        isMaxQuantity: false,
        maxQuantity: categoryConfig.quantity,
      });
      return this.selectedItems.get(categoryId);
    }
    return category;
  }

  /**
   * @description Get an item from a category
   * @param {number} itemId
   * @param {string} categoryId
   * @returns {FoxSellSelectedItem | undefined}
   */
  getItem(itemId, categoryId) {
    const selectedCategory = this.getCategory(categoryId);
    if (!selectedCategory) return undefined;

    return selectedCategory.items.get(itemId);
  }

  /**
   * @description Get the selected items
   * @returns {FoxSellSerializedCategory[]}
   */
  getSelectedItems() {
    return Array.from(this.selectedItems.values()).map(category => ({
      id: category.id,
      title: category.title,
      quantity: category.quantity,
      maxQuantity: category.maxQuantity,
      isMaxQuantity: category.isMaxQuantity,
      price: category.price,
      items: Array.from(category.items.values())
    }));
  }

  /**
   * @description Get the selected add-ons
   * @returns {FoxSellSelectedItem[]}
   */
  getSelectedAddOns() {
    return Array.from(this.selectedAddOns.values());
  }

  /**
   * @description Add an item to a category
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {string} categoryId
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  addToBundle(item, quantity, categoryId, dispatchEvent = true) {
    const selectedCategory = this.getCategory(categoryId, true);
    if (!selectedCategory || !this.config) return;

    const qaoEnabled = this.config.options.length > 0;
    if (((selectedCategory.quantity + quantity) > selectedCategory.maxQuantity) && !qaoEnabled) return;

    // Inventory check: prevent adding more than available stock
    if (item.inventory_management && item.inventory_policy !== 'continue') {
      const currentQuantity = this.getItem(item.id, categoryId)?.quantity ?? 0;
      if ((currentQuantity + quantity) > item.inventory_quantity) return;
    }

    const selectedItem = this.getItem(item.id, categoryId);
    if (!selectedItem) {
      selectedCategory.items.set(item.id, {
        ...item,
        quantity: quantity,
      });
    } else {
      selectedItem.quantity += quantity;
    }
    selectedCategory.quantity += quantity;

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'add',
        item: this.getItem(item.id, categoryId),
        category: this.bundle.items.find((c) => c.id === categoryId),
      }
    }));
  }

  /**
   * @description Remove an item from a category
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {string} categoryId
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  removeFromBundle(item, quantity, categoryId, dispatchEvent = true) {
    const selectedCategory = this.getCategory(categoryId);
    if (!selectedCategory) return;

    const selectedItem = this.getItem(item.id, categoryId);
    if (!selectedItem) return;

    const newItemQuantity = Math.max(selectedItem.quantity - quantity, 0);
    if (newItemQuantity === 0) {
      selectedCategory.items.delete(item.id);
    } else {
      selectedItem.quantity = newItemQuantity;
    }

    selectedCategory.quantity = Math.max(selectedCategory.quantity - quantity, 0);
    const categoryForEvent = selectedCategory.quantity <= 0
      ? { ...selectedCategory, isMaxQuantity: false, items: Array.from(selectedCategory.items.values()) }
      : null;

    if (selectedCategory.quantity <= 0) {
      this.selectedItems.delete(categoryId);
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    const category = categoryForEvent ?? this.bundle.items.find((c) => c.id === categoryId);
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'remove',
        item: { ...selectedItem, quantity: newItemQuantity },
        category,
      }
    }));
  }

  /**
   * @description Clear the bundle
   * @param {boolean} [dispatchEvent=true]
   * @returns {void}
   */
  clearBundle(dispatchEvent = true) {
    this.selectedItems.clear();
    this.validateBundle();
    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'clear'
      }
    }));
  }

  /**
   * @description Add an add-on to the selected add-ons
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  addToAddOns(item, quantity, dispatchEvent = true) {
    if (!item || !quantity || quantity <= 0) return;

    const existingAddOn = this.selectedAddOns.get(item.id);
    if (existingAddOn) {
      existingAddOn.quantity += quantity;
    } else {
      this.selectedAddOns.set(item.id, {
        ...item,
        quantity: quantity,
      });
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'add-addon',
        item: this.selectedAddOns.get(item.id),
      }
    }));
  }

  /**
   * @description Remove an add-on from the selected add-ons
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  removeFromAddOns(item, quantity, dispatchEvent = true) {
    if (!item || !quantity || quantity <= 0) return;

    const existingAddOn = this.selectedAddOns.get(item.id);
    if (!existingAddOn) return;

    const newQuantity = Math.max(existingAddOn.quantity - quantity, 0);
    if (newQuantity === 0) {
      this.selectedAddOns.delete(item.id);
    } else {
      existingAddOn.quantity = newQuantity;
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'remove-addon',
        item: {
          ...existingAddOn,
          quantity: newQuantity,
        },
      }
    }));
  }

  /**
   * @description Clear all add-ons
   * @param {boolean} [dispatchEvent=true]
   * @returns {void}
   */
  clearAddOns(dispatchEvent = true) {
    this.selectedAddOns.clear();
    if (!dispatchEvent) return;
    this.validateBundle();
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'clear-addons',
      }
    }));
  }

  /**
   * @description Validate the bundle
   * @returns {void}
   */
  validateBundle() {
    if (!this.config) return;
    const qaoEnabled = this.config.options?.length > 0;
    const isItemsValid = qaoEnabled ? this.validateBundleWithQAO() : this.validateBundleWithoutQAO();
    const isAddOnsValid = this.validateAddOns(isItemsValid);

    const isValid = isItemsValid && isAddOnsValid;
    this.bundle = this.buildBundle(isValid, isAddOnsValid, isItemsValid);

    this.updateLineItemProperties();
    this.renderPrice();
    this.toggleAddToCartButton(!isValid);
  }

  /**
   * @description Validate the selected add-ons
   * @param {boolean} isItemsValid
   * @returns {boolean}
   */
  validateAddOns(isItemsValid) {
    if (!this.config) return false;
    const { allowedIds, maximum, addOnStrategy } = this.getAddOnsConfig();

    if(addOnStrategy === 'automatic_add') {
      if(isItemsValid) {
        this.autoAddAddOns();
      } else {
        this.clearAddOns(false);
      }
      return true;
    }

    let selectedQuantity = 0;
    let refreshBundleState = false;

    for (const selectedAddOn of this.getSelectedAddOns()) {
      if (!allowedIds.includes(selectedAddOn.product.id)) {
        this.removeFromAddOns(selectedAddOn, selectedAddOn.quantity, false);
        refreshBundleState = true;
      } else {
        if((selectedQuantity + selectedAddOn.quantity) > maximum) {
          this.removeFromAddOns(selectedAddOn, selectedAddOn.quantity, false);
          refreshBundleState = true;
        } else {
          selectedQuantity = selectedQuantity + selectedAddOn.quantity;
        }
      }
    }

    if (refreshBundleState) {
      this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
        detail: {
          bundle: this.bundle,
          action: 'refresh',
        }
      }));
    }

    const selectedAddOns = this.getSelectedAddOns();
    let { minimum: minimumAddOns, maximum: maximumAddOns } = this.getAddOnsConfig();
    const selectedAddOnsQuantity = selectedAddOns.reduce((sum, item) => sum + item.quantity, 0);
    let isValid = selectedAddOnsQuantity >= minimumAddOns && selectedAddOnsQuantity <= maximumAddOns;

    return isValid;
  }

  /**
   * @description Auto add add-ons to bundle
   */
  autoAddAddOns() {
    if(!this.config) return;
    const { allowedIds, maximum } = this.getAddOnsConfig();

    this.clearAddOns(false);

    let selectedQuantity = 0;
    for (const allowedId of allowedIds) {
      const item = this.config.addOnProducts.find(item => item.id === allowedId);
      if(item && item.variants[0]) {
        this.selectedAddOns.set(item.variants[0].id, {
          ...item.variants[0],
          foxsell_price: getAddOnPrice(item.id, item.variants[0].id, this.config.addOnProductProperties),
          product: {
            ...item,
            featured_image: { 
              src: item.featured_image, 
              alt: item.title
            },
          },
          quantity: 1,
        });
        selectedQuantity = selectedQuantity + 1;
        if(selectedQuantity >= maximum) break;
      }
    }
  }

  /**
   * @description Validate the bundle with QAO
   * @returns {boolean}
   */
  validateBundleWithQAO() {
    if (!this.config || !this.config.options) {
      this.toggleAddToCartButton(true);
      return false;
    }

    const optionLimits = (this.config.options || [])
      .map(opt => Number(opt.quantity ?? opt))
      .filter(n => !Number.isNaN(n));

    optionLimits.length ? Math.max(...optionLimits) : 0;
    const minQuantity = optionLimits.length ? Math.min(...optionLimits) : 0;

    const items = this.getSelectedItems().flatMap(category =>
      Array.isArray(category.items) ? category.items : []
    );

    const itemsCount = items.reduce((sum, item) => sum + item.quantity, 0);

    let isValid = false;

    {
      isValid = itemsCount >= minQuantity;
    }

    this.selectedItems.forEach(category => {
      category.isMaxQuantity = false;
    });

    return isValid;
  }

  /**
   * @description Validate the bundle without QAO
   * @returns {boolean}
   */
  validateBundleWithoutQAO() {
    if (!this.config || !this.config.categories) {
      this.toggleAddToCartButton(true);
      return false;
    }

    const isValid = this.config.categories.reduce((allValid, category) => {
      const isOptional = category.quantity === 0;
      // If the category is optional, we don't need to validate it
      if (isOptional) return allValid;
      const selectedCategory = this.getCategory(category.id);
      if (!selectedCategory) return false;
      const atMax = selectedCategory.quantity >= selectedCategory.maxQuantity;
      selectedCategory.isMaxQuantity = atMax;
      return allValid && atMax;
    }, true);

    return isValid;
  }

  /**
   * @description Get allowed add-on variant IDs from config with min/max values
   * @returns {FoxSellAddOnsConfig}
   */
  getAddOnsConfig() {
    if(!this.config) return { ...emptyAddOnsConfig };
    //! support add-on strategy: add_on_step, automatic_add
    const addOnStrategy = this.config.additionalSettings['add_on_settings'].strategy ?? 'add_on_step';

    /** @type {number[]} */
    const allowedIds = Object.keys(this.config.addOnProductProperties).map(key => getItemIdFromGid(key));

    let minimum = 0;
    let maximum = 0;

    if(this.config.options.length > 0) {
      const currentValidOption = this.getCurrentValidOption();
      if(!currentValidOption) return { ...emptyAddOnsConfig };
      minimum = currentValidOption.add_on.minimum ?? 0;
      maximum = currentValidOption.add_on.maximum ?? 0;
    } else {
      minimum = this.config.settings.addOn.minimum ?? 0;
      maximum = this.config.settings.addOn.maximum ?? 0;
    }

    const isMaxedOut = this.getSelectedAddOns().reduce((sum, item) => sum + item.quantity, 0) >= maximum;
    const selectedQuantity = this.getSelectedAddOns().reduce((sum, item) => sum + item.quantity, 0);
    return {
      addOnStrategy: addOnStrategy,
      allowedIds: allowedIds,
      enabled: allowedIds.length > 0,
      minimum,
      maximum,
      selectedQuantity,
      isMaxedOut
    };
  }

  /**
   * @description Update the line item properties
   * @returns {void}
   */
  updateLineItemProperties() {
    const bundleIdInput = this.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_id]"]');
    const itemInput = this.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_items]"]');
    const savingsInput = this.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_savings]"]');
    const idInput = this.querySelector('input[name="id"]');

    if (!itemInput || !bundleIdInput || !this.config?.bundleId) return;

    bundleIdInput.setAttribute('value', this.bundle.id);

    if (this.bundle.qaoEnabled) {
      const validOption = this.getCurrentValidOption();
      if (idInput) {
        idInput.setAttribute('value', validOption?.variant_id ?? '');
      }
    }

    const lineItems = this.bundle.items.flatMap(category =>
      (category.items || []).map(item => ({
        variantId: Number(item.id) || item.id,
        quantity: item.quantity || 1,
        category: category.title,
        type: 'product',
        properties: item.properties || {}
      }))
    );

    const addOnLineItems = this.bundle.addOns.items.map(item => ({
      variantId: Number(item.id) || item.id,
      quantity: item.quantity || 1,
      category: '',
      type: 'addOns',
      properties: item.properties || {}
    }));

    lineItems.push(...addOnLineItems);

    itemInput.setAttribute('value', JSON.stringify(lineItems));

    if (savingsInput) {
      const { totalDiscount, totalPrice } = this.bundle;
      if (totalDiscount > 0 && totalPrice > 0) {
        savingsInput.setAttribute('value', window.foxsell?.formatMoney?.(totalDiscount));
      } else {
        savingsInput.setAttribute('value', '');
      }
    }
  }

  /**
   * @description Get the total price of the bundle
   * @returns {FoxSellTotalPrice}
   */
  getTotalPrice() {
    if (!this.config) return { ...emptyTotalPrice };

    const itemsTotalPrice = this.getSelectedItems().reduce((sum, category) => {
      return sum + category.items.reduce((sum, item) => {
        return sum + (item.foxsell_price ?? item.price) * item.quantity;
      }, 0);
    }, 0);

    const addOnsTotalPrice = this.getSelectedAddOns().reduce((sum, item) => {
      return sum + (item.foxsell_price ?? item.price) * item.quantity;
    }, 0);

    let discountValue = 0;
    let totalDiscount = 0;
    let originalTotalPrice = 0;
    let totalPrice = 0;

    const priceStrategy = this.config.settings.price.strategy;
    const qaoEnabled = this.config.options.length > 0;

    if(priceStrategy === 'dynamic_pricing') {
      originalTotalPrice = itemsTotalPrice + addOnsTotalPrice;
      if(qaoEnabled) {
        const validOption = this.getCurrentValidOption();
        if(validOption) {
          discountValue = validOption.price.value;
        }
      } else {
        discountValue = this.config.settings.price.value;
      }
      totalDiscount = (discountValue / 100) * itemsTotalPrice;
      totalPrice = (itemsTotalPrice - totalDiscount) + addOnsTotalPrice;
    } else {
      if(qaoEnabled) {
        const validOption = this.getCurrentValidOption();
        if(validOption) {
          const { price, compareAtPrice } = getVariantPrice(this.config.variants, validOption);
          totalPrice = price + addOnsTotalPrice;
          originalTotalPrice = compareAtPrice + addOnsTotalPrice;
          totalDiscount = Math.max(originalTotalPrice - totalPrice, 0);
        }
      } else {
        totalPrice = (this.config.settings.price.value * 100) + addOnsTotalPrice;
        originalTotalPrice = (this.config.variants[0]?.compare_at_price ?? 0) + addOnsTotalPrice;
        totalDiscount = Math.max(originalTotalPrice - totalPrice, 0);
      }
    }

    return {
      originalTotalPrice,
      totalPrice,
      totalDiscount,
      discountValue,
      priceStrategy,
      itemsTotalPrice,
      addOnsTotalPrice
    };
  }

  /**
   * @description Render the price of the bundle on add-to-cart button
   * @returns {void}
   */
  renderPrice() {
    const priceWrapper = document.querySelector('.product-price-wrapper');
    const { originalTotalPrice, totalPrice } = this.bundle;
    if(priceWrapper) {
      if (originalTotalPrice > totalPrice) {
        priceWrapper.innerHTML = `
        <span class="price" data-product-price="">${window.foxsell?.formatMoney?.(totalPrice)}</span>
        <span class="compare-at-price" data-compare-price="" aria-label="Original price">${window.foxsell?.formatMoney?.(originalTotalPrice)}</span>
        `;
      } else {
        priceWrapper.innerHTML = `<span class="price" data-product-price="">${window.foxsell?.formatMoney?.(totalPrice)}</span>`;
      }
    }
  }

  /**
   * @description Toggle the add to cart button disabled state
   * @param {boolean} disable
   * @returns {void}
   */
  toggleAddToCartButton(disable) {
    const addToCartButton = this.querySelector('button[type="submit"]');
    if (!addToCartButton) return;
    addToCartButton.toggleAttribute('disabled', disable);
  }
}

// @ts-check

class MellowMixMatch extends FoxSellMixMatch {

  /**
   * @description Add an item to a category
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {string} categoryId
   * @param {string} productIndex
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  addToBundleWithIndex(item, quantity, categoryId, productIndex, dispatchEvent = true) {
    const selectedCategory = this.getCategory(categoryId, true);
    if (!selectedCategory || !this.config) return;

    const qaoEnabled = this.config.options.length > 0;
    if (((selectedCategory.quantity + quantity) > selectedCategory.maxQuantity) && !qaoEnabled) return;

    const itemId = parseInt(`${item.id}${productIndex}`);

    // Inventory check: prevent adding more than available stock
    if (item.inventory_management && item.inventory_policy !== 'continue') {
      const currentQuantity = this.getItem(itemId, categoryId)?.quantity ?? 0;
      if ((currentQuantity + quantity) > item.inventory_quantity) return;
    }

    const selectedItem = this.getItem(itemId, categoryId);
    if (!selectedItem) {
      selectedCategory.items.set(itemId, {
        ...item,
        quantity: quantity,
        properties: {
          '__index__': productIndex,
        }
      });
    } else {
      selectedItem.quantity += quantity;
    }
    selectedCategory.quantity += quantity;

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'add',
        item: this.getItem(itemId, categoryId),
        category: this.bundle.items.find((c) => c.id === categoryId),
      }
    }));
  }

  /**
   * @description Add an add-on to the selected add-ons
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {string} productIndex
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  addToAddOnsWithIndex(item, quantity, productIndex, dispatchEvent = true) {
    if (!item || !quantity || quantity <= 0) return;

    const itemId = parseInt(`${item.id}${productIndex}`);

    const existingAddOn = this.selectedAddOns.get(itemId);
    if (existingAddOn) {
      existingAddOn.quantity += quantity;
    } else {
      this.selectedAddOns.set(itemId, {
        ...item,
        quantity: quantity,
        properties: {
          '__index__': productIndex,
        }
      });
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'add-addon',
        item: this.selectedAddOns.get(itemId),
      }
    }));
  }


  /**
   * @description Remove an item from a category
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {string} categoryId
   * @param {string} productIndex
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  removeFromBundleWithIndex(item, quantity, categoryId, productIndex, dispatchEvent = true) {
    const selectedCategory = this.getCategory(categoryId);
    if (!selectedCategory) return;

    const itemId = parseInt(`${item.id}${productIndex}`);

    const selectedItem = this.getItem(itemId, categoryId);
    if (!selectedItem) return;

    const newItemQuantity = Math.max(selectedItem.quantity - quantity, 0);
    if (newItemQuantity === 0) {
      selectedCategory.items.delete(itemId);
    } else {
      selectedItem.quantity = newItemQuantity;
    }

    selectedCategory.quantity = Math.max(selectedCategory.quantity - quantity, 0);
    const categoryForEvent = selectedCategory.quantity <= 0
      ? { ...selectedCategory, isMaxQuantity: false, items: Array.from(selectedCategory.items.values()) }
      : null;

    if (selectedCategory.quantity <= 0) {
      this.selectedItems.delete(categoryId);
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    const category = categoryForEvent ?? this.bundle.items.find((c) => c.id === categoryId);
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'remove',
        item: { ...selectedItem, quantity: newItemQuantity },
        category,
      }
    }));
  }

  /**
   * @description Remove an add-on from the selected add-ons
   * @param {FoxSellVariant} item
   * @param {number} quantity
   * @param {string} productIndex
   * @param {boolean} dispatchEvent
   * @returns {void}
   */
  removeFromAddOnsWithIndex(item, quantity, productIndex, dispatchEvent = true) {
    if (!item || !quantity || quantity <= 0) return;

    const itemId = parseInt(`${item.id}${productIndex}`);

    const existingAddOn = this.selectedAddOns.get(itemId);
    if (!existingAddOn) return;

    const newQuantity = Math.max(existingAddOn.quantity - quantity, 0);
    if (newQuantity === 0) {
      this.selectedAddOns.delete(itemId);
    } else {
      existingAddOn.quantity = newQuantity;
    }

    this.validateBundle();

    if (!dispatchEvent) return;
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.bundle,
        action: 'remove-addon',
        item: {
          ...existingAddOn,
          quantity: newQuantity,
        },
      }
    }));
  }


  /**
   * @description Render the price of the bundle on add-to-cart button
   * @returns {void}
   */
  renderPrice() {
    const priceWrapper = document.querySelector('.product-price-wrapper');
    const fixedBundlePriceWrapper = document.querySelector('.foxsell-mix-match__fixed-bundle-price');
    const priceStrategy = this.config?.settings.price.strategy;
    const quantity = parseInt(
      /** @type {HTMLInputElement | null} */ (this.querySelector('form input[name="quantity"]'))?.value ?? '1',
      10
    );

    if(priceStrategy === 'fixed_pricing') {
      const { originalTotalPrice, totalPrice } = this.bundle;
      const finalSalePrice = totalPrice * quantity;
      const finalCompareAtPrice = originalTotalPrice * quantity;

      if(this.dataset.renderPrice === 'true') {
        if(priceWrapper) {
          if (finalCompareAtPrice > finalSalePrice) {
            priceWrapper.innerHTML = `
            <span class="price" data-product-price="">${window.foxsell?.formatMoney?.(finalSalePrice)}</span>
            <span class="compare-at-price" data-compare-price="" aria-label="Original price">${window.foxsell?.formatMoney?.(finalCompareAtPrice)}</span>
            `;
          } else {
            priceWrapper.innerHTML = `<span class="price" data-product-price="">${window.foxsell?.formatMoney?.(finalSalePrice)}</span>`;
          }
        }
      }

      if(fixedBundlePriceWrapper) {
        if (finalCompareAtPrice > finalSalePrice) {
          fixedBundlePriceWrapper.innerHTML = `
          <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(finalSalePrice)}</span>
          <span class="foxsell-slashed-price">${window.foxsell?.formatMoney?.(finalCompareAtPrice)}</span>
          `;
        } else {
          fixedBundlePriceWrapper.innerHTML = `<span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(finalSalePrice)}</span>`;
        }
      }
    } else {
      let itemsCompareAtPrice = 0;
      let itemsSalePrice = 0;
      itemsSalePrice = this.getSelectedItems().reduce((sum, category) => {
        return sum + category.items.reduce((sum, item) => {
          return sum + (item.foxsell_price * item.quantity);
        }, 0);
      }, 0);

      if(this.config?.useCompareAtPrice) {
        itemsCompareAtPrice = this.getSelectedItems().reduce((sum, category) => {
          return sum + category.items.reduce((sum, item) => {
            return sum + ((item.compare_at_price ?? item.price) * item.quantity);
          }, 0);
        }, 0);
      } else {
        itemsCompareAtPrice = this.getSelectedItems().reduce((sum, category) => {
          return sum + category.items.reduce((sum, item) => {
            return sum + ((item.price) * item.quantity);
          }, 0);
        }, 0);

        if(itemsSalePrice === itemsCompareAtPrice) {
          itemsCompareAtPrice = this.getSelectedItems().reduce((sum, category) => {
            return sum + category.items.reduce((sum, item) => {
              return sum + ((item.compare_at_price ?? item.price) * item.quantity);
            }, 0);
          }, 0);
        }
      }

      let addOnsCompareAtPrice = 0;
      let addOnsSalePrice = 0;
      
      addOnsSalePrice = this.getSelectedAddOns().reduce((sum, addOn) => {
        return sum + ((addOn.foxsell_price) * addOn.quantity);
      }, 0);

      if(this.config?.useCompareAtPrice) {
        addOnsCompareAtPrice = this.getSelectedAddOns().reduce(
          (sum, addOn) => sum + ((addOn.compare_at_price ?? addOn.price ?? 0) * addOn.quantity),
          0
        ) ?? 0;
      } else {
        addOnsCompareAtPrice = this.getSelectedAddOns().reduce(
          (sum, addOn) => sum + ((addOn.price ?? 0) * addOn.quantity),
          0
        ) ?? 0;

        if(addOnsSalePrice === addOnsCompareAtPrice) {
          addOnsCompareAtPrice = this.getSelectedAddOns().reduce(
            (sum, addOn) => sum + ((addOn.compare_at_price ?? addOn.price ?? 0) * addOn.quantity),
            0
          ) ?? 0;
        }
      }

      const finalSalePrice = (itemsSalePrice + addOnsSalePrice) * quantity;
      const finalCompareAtPrice = (itemsCompareAtPrice + addOnsCompareAtPrice) * quantity;


      if(fixedBundlePriceWrapper) {
        if(finalCompareAtPrice > finalSalePrice) {
          fixedBundlePriceWrapper.innerHTML = `
          <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(finalSalePrice)}</span>
          <span class="foxsell-slashed-price">${window.foxsell?.formatMoney?.(finalCompareAtPrice)}</span>
          `;
        } else {
          fixedBundlePriceWrapper.innerHTML = `<span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(finalSalePrice)}</span>`;
        }
      }

      if(this.dataset.renderPrice === 'true') {
        if(priceWrapper) {
          if(finalCompareAtPrice > finalSalePrice) {
            priceWrapper.innerHTML = `
            <span class="price" data-product-price="">${window.foxsell?.formatMoney?.(finalSalePrice)}</span>
            <span class="compare-at-price" data-compare-price="" aria-label="Original price">${window.foxsell?.formatMoney?.(finalCompareAtPrice)}</span>
            `;
          } else {
            priceWrapper.innerHTML = `<span class="price" data-product-price="">${window.foxsell?.formatMoney?.(finalSalePrice)}</span>`;
          }
        }
      }
    }
  }
}

class FixedBundleQuantity extends HTMLElement {
  constructor() {
    super();
    /** @type {NodeListOf<HTMLButtonElement>} */
    this.quantityButtons = this.querySelectorAll('.foxsell-mix-match__fixed-bundle-quantity-button');

    /** @type {HTMLInputElement | null} */
    this.quantityInput = this.querySelector('.foxsell-mix-match__fixed-bundle-quantity-input');

    /** @type {HTMLInputElement | null} */
    this.formQuantityInput = this.closest('foxsell-mix-match')?.querySelector('form input[name="quantity"]') ?? null;
    this._boundHandleQuantityUpdate = this.handleQuantityUpdate.bind(this);

    /** @type {number} */
    this.currentQuantity = 1;
  }

  connectedCallback() {
    if(this.quantityButtons) {
      this.quantityButtons.forEach(button => {
        button.addEventListener('click', this._boundHandleQuantityUpdate);
      });
    }
  }

  disconnectedCallback() {
    if(this.quantityButtons) {
      this.quantityButtons.forEach(button => {
        button.removeEventListener('click', this._boundHandleQuantityUpdate);
      });
    }
  }

  /**
   * @description Handle the quantity update
   * @param {Event} event
   * @returns {void}
   */
  handleQuantityUpdate(event) {
    const target = /** @type {HTMLButtonElement} */(event.target);
    const action = target.dataset.action;
    this.currentQuantity = parseInt(this.quantityInput?.value ?? '1');

    /** @type {NodeListOf<HTMLSpanElement>} */
    this.titleQuantityElements = document.querySelectorAll('.foxsell-product-card__title-quantity');

    if(this.quantityInput) {
      if(action === 'subtract') {
        this.currentQuantity = Math.max(this.currentQuantity - 1, 1);
      } else if(action === 'add') {
        this.currentQuantity = this.currentQuantity + 1;
      }
    }
    
    if(this.quantityInput) {
      this.quantityInput.value = this.currentQuantity.toString();
    }

    if(this.formQuantityInput) {
      this.formQuantityInput.value = this.currentQuantity.toString();
    }

    if(this.titleQuantityElements.length > 0) {
      this.titleQuantityElements.forEach(element => {
        element.dataset.productQuantity = this.currentQuantity.toString();
        element.textContent = `${this.currentQuantity}x`;
      });
    }

    this.updateButtonStates();

    /** @type {FoxSellMixMatch | null} */
    const foxsellMixMatch = this.closest('foxsell-mix-match');
    if(foxsellMixMatch) {
      foxsellMixMatch.renderPrice();
    }
  }

  updateButtonStates() {
    if(this.currentQuantity === 1) {
      this.quantityButtons.forEach(button => {
        if(button.dataset.action === 'subtract') {
          button.setAttribute('disabled', 'disabled');
        }
      });
    } else {
      this.quantityButtons.forEach(button => {
        if(button.dataset.action === 'subtract') {
          button.removeAttribute('disabled');
        }
      });
    }
  }
}

// @ts-check

class FoxSellCategoryHeader extends HTMLElement {
  constructor() {
    super();
    this.categoryId = this.getAttribute('data-category-id');
    this.quantityElement = this.querySelector('.foxsell-category__quantity');
    this.foxsell = this.closest('foxsell-mix-match');
    this._boundUpdateQuantity = this.updateQuantity.bind(this);
  }

  connectedCallback() {
    if (!this.foxsell) return;
    this.foxsell.addEventListener(FOXSELL_EVENTS.bundleUpdated, this._boundUpdateQuantity);
  }

  disconnectedCallback() {
    if (!this.foxsell || !this._boundUpdateQuantity) return;
    this.foxsell.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this._boundUpdateQuantity);
  }

  /**
   * @description Update the category quantity
   * @param {CustomEvent} event
   * @returns {void}
   */
  updateQuantity(event) {
    if (this.categoryId === '__add_ons__') {
      this.updateAddOnQuantity(event);
      return;
    }
    const { category } = event.detail;
    if (!category) return;
    if (category.id !== this.categoryId) return;
    if (!this.quantityElement) return;
    this.quantityElement.textContent = `${category.quantity}/${category.maxQuantity}`;
  }

  /**
   * @description Update the add-on quantity
   * @param {CustomEvent} event
   * @returns {void}
   */
  updateAddOnQuantity(event) {
    const { bundle } = event.detail;
    if (!bundle) return;
    if (!this.quantityElement) return;
    this.quantityElement.textContent = `${bundle.addOns.selectedQuantity}/${bundle.addOns.maximum}`;
  }
}

// @ts-check

class FoxSellProductCard extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');

    /** @type {FoxSellVariantRadio | FoxSellVariantSelect | null} */
    this.variantSelector = this.querySelector('foxsell-variant-radio') || this.querySelector('foxsell-variant-select');
    this.disableAddToBundle = false;
    this.boundAddToBundle = this.addToBundle.bind(this);
    this.boundRemoveFromBundle = this.removeFromBundle.bind(this);
    this.boundHandleVariantChange = this.handleVariantChange.bind(this);
    this.boundHandleBundleUpdated = this.handleBundleUpdated.bind(this);
    this.categoryId = this.getAttribute('data-category') || null;
    this.productId = parseInt(this.getAttribute('data-product-id') || '0');
    this.isAddOnCard = this.categoryId === '__add_ons__';

    if (this.isAddOnCard) {
      this.disableAddToBundle = true;
      this.toggleAddToBundleButton(true);
    }
  }

  connectedCallback() {
    this.querySelectorAll('.add-to-bundle').forEach(btn => btn.addEventListener('click', this.boundAddToBundle));
    this.querySelectorAll('.remove-from-bundle').forEach(btn => btn.addEventListener('click', this.boundRemoveFromBundle));
    if (this.variantSelector) {
      this.addEventListener('variant-change', this.boundHandleVariantChange);
    }

    if (this.foxsell) {
      this.foxsell.addEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdated);
    }
  }

  disconnectedCallback() {
    this.querySelectorAll('.add-to-bundle').forEach(btn => btn.removeEventListener('click', this.boundAddToBundle));
    this.querySelectorAll('.remove-from-bundle').forEach(btn => btn.removeEventListener('click', this.boundRemoveFromBundle));
    if (this.variantSelector) {
      this.removeEventListener('variant-change', this.boundHandleVariantChange);
    }

    if (this.foxsell) {
      this.foxsell.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdated);
    }
  }

  handleVariantChange() {
    this.updateFeaturedImage();
    this.updatePrice();
    this.updateQuantity(this.getCurrentQuantity());

    if (!this.variantSelector?.currentVariant) {
      this.disableAddToBundle = true;
      this.toggleAddToBundleButton(true);
      return;
    }

    let disable;
    if (this.categoryId === '__add_ons__') {
      const bundle = this.foxsell?.bundle;
      if (!bundle) {
        disable = true;
      } else {
        const { enabled, allowedIds, isMaxedOut } = bundle.addOns;
        const isAllowed = allowedIds.includes(this.variantSelector.product?.id ?? 0);
        disable = !enabled || !isAllowed || isMaxedOut || this.isAddOnAtInventoryLimit();
      }
    } else {
      const category = this.foxsell?.getCategory(this.categoryId ?? '');
      disable = (category?.isMaxQuantity ?? false) || this.isVariantAtInventoryLimit();
    }

    this.disableAddToBundle = disable;
    this.toggleAddToBundleButton(disable);
  }

  /**
   * @description Handle the bundle updated event
   * @param {FoxSellCustomEvent} event
   * @returns {void}
   */
  handleBundleUpdated(event) {
    if (event.detail.action === 'clear') {
      this.updateQuantity(0);
      const atInventoryLimit = this.categoryId === '__add_ons__'
        ? this.isAddOnAtInventoryLimit()
        : this.isVariantAtInventoryLimit();
      this.disableAddToBundle = atInventoryLimit;
      this.toggleAddToBundleButton(atInventoryLimit);
      return;
    }

    if (this.categoryId === '__add_ons__') {
      this.handleAddOnUpdated(event);
      return;
    }

    if (!this.foxsell || !this.foxsell.config) return;

    const qaoEnabled = this.foxsell.config.options.length > 0;

    if (event.detail.action === 'refresh') {
      this.updateQuantity(this.getCurrentQuantity());
      if (qaoEnabled) this.updatePrice();
      return;
    }

    if (event.detail.action !== 'add' && event.detail.action !== 'remove') return;

    const { category, item } = event.detail;
    const disableAddToBundle = category.isMaxQuantity;

    if (!qaoEnabled && this.categoryId !== category.id) return;

    this.disableAddToBundle = disableAddToBundle || this.isVariantAtInventoryLimit();
    this.toggleAddToBundleButton(this.disableAddToBundle);

    if (qaoEnabled) this.updatePrice();

    if (this.productId === item.product.id) {
      this.updateQuantity(this.getCurrentQuantity());
    }
  }

  /**
   * @description Handle the add-on updated event
   * @param {FoxSellCustomEvent} event
   * @returns {void}
   */
  handleAddOnUpdated(event) {
    const { action } = event.detail;

    if (action === 'clear-addons') {
      this.updateQuantity(0);
      return;
    }

    if (action === 'refresh') {
      this.updateQuantity(this.getCurrentQuantity());
      return;
    }

    const { item, bundle } = event.detail;
    if (!item || !bundle) return;

    const { enabled, allowedIds, isMaxedOut } = bundle.addOns;

    const isAllowed = allowedIds.includes(this.variantSelector?.product?.id ?? 0);

    if (isAllowed) {
      this.classList.add('active');
    } else {
      this.classList.remove('active');
    }

    const disableAddToBundle = !enabled || !isAllowed || isMaxedOut;

    this.disableAddToBundle = disableAddToBundle || this.isAddOnAtInventoryLimit();
    this.toggleAddToBundleButton(disableAddToBundle);

    if (this.productId === item.product.id) {
      this.updateQuantity(this.getCurrentQuantity());
    }
  }

  /**
   * @description Get the current quantity
   * @returns {number}
   */
  getCurrentQuantity() {
    const variantId = this.variantSelector?.currentVariant?.id;
    if (variantId === undefined || variantId === null || !this.foxsell?.bundle || !this.categoryId) return 0;

    /* check if the variant is an add-on */
    if (this.categoryId === '__add_ons__') {
      const addOns = this.foxsell.bundle.addOns;
      if (!addOns || !addOns.items) return 0;
      return addOns.items.find((item) => item.id === variantId)?.quantity ?? 0;
    }

    /* else if it is a regular product */
    const category = this.foxsell.bundle.items.find((c) => c.id === this.categoryId);
    if (!category) return 0;
    return category.items.find((item) => item.id === variantId)?.quantity ?? 0;
  }

  /**
   * @description Update the featured image
   * @returns {void}
   */
  updateFeaturedImage() {
    if (!this.variantSelector || !this.variantSelector.currentVariant) return;
    const img = this.querySelector('.foxsell-product-card__image');
    const image = this.variantSelector.currentVariant?.featured_image || this.variantSelector.currentVariant?.product?.featured_image;
    if (!img || !image?.src) return;
    img.setAttribute('src', image.src);
    if (image.width != null) img.setAttribute('srcset', image.src + (image.src.includes('?') ? '&' : '?') + 'width=' + image.width);
  }

  /**
   * @description Update the price
   * @returns {void}
   */
  updatePrice() {
    if (!this.variantSelector || !this.foxsell || !this.foxsell.bundle) return;
    const priceEl = this.querySelector('.foxsell-product-card__price');
    if (!priceEl) return;

    const { currentVariant, product } = this.variantSelector;
    let price = currentVariant?.foxsell_price ?? currentVariant?.product?.price ?? product?.price ?? 0;

    let discountedPrice = price;
    const priceStrategy = this.foxsell.bundle.priceStrategy;
    if (!this.isAddOnCard && priceStrategy && priceStrategy.strategy === 'dynamic_pricing') {
      const discount = priceStrategy.value;
      discountedPrice = price - (price * (discount / 100));
    }

    if (price > discountedPrice) {
      priceEl.innerHTML = `
      <span>${window.foxsell?.formatMoney?.(discountedPrice)}</span>
      <span class="foxsell-slashed-price">${window.foxsell?.formatMoney?.(price)}</span>`;
    } else {
      priceEl.innerHTML = `<span>${window.foxsell?.formatMoney?.(price)}</span>`;
    }
  }

  /**
   * @description Update the quantity
   * @param {number} quantity
   * @returns {void}
   */
  updateQuantity(quantity) {
    const el = this.querySelector('.quantity');
    if (el) el.textContent = String(quantity);
  }

  /**
   * @description Check if the current variant has reached its inventory limit
   * @returns {boolean}
   */
  isVariantAtInventoryLimit() {
    const variant = this.variantSelector?.currentVariant;
    if (!variant || !this.foxsell || !this.categoryId) return true;
    // No limit if inventory isn't tracked or policy allows overselling
    if (!variant.inventory_management || variant.inventory_policy === 'continue') return false;
    const currentQuantity = this.getCurrentQuantity();
    return currentQuantity >= variant.inventory_quantity;
  }

  /**
   * @description Check if the current add-on variant has reached its inventory limit
   * @returns {boolean}
   */
  isAddOnAtInventoryLimit() {
    const variant = this.variantSelector?.currentVariant;
    if (!variant || !this.foxsell || !this.categoryId) return true;
    if (!variant.inventory_management || variant.inventory_policy === 'continue') return false;
    const currentQuantity = this.getCurrentQuantity() ?? 0;
    return currentQuantity >= variant.inventory_quantity;
  }

  /**
   * @description Add item to bundle
   * @returns {void}
   */
  addToBundle() {
    if (!this.variantSelector || !this.variantSelector.currentVariant || !this.foxsell || !this.categoryId) return;
    if (this.categoryId === '__add_ons__') {
      // @ts-ignore
      this.foxsell.addToAddOns(this.variantSelector.currentVariant, 1);
      if (this.isAddOnAtInventoryLimit()) {
        this.toggleAddToBundleButton(true);
      }
    } else {
      this.foxsell.addToBundle(this.variantSelector.currentVariant, 1, this.categoryId);
      if (this.isVariantAtInventoryLimit()) {
        this.toggleAddToBundleButton(true);
      }
    }
  }

  /**
   * @description Remove item from bundle
   * @returns {void}
   */
  removeFromBundle() {
    if (!this.variantSelector || !this.variantSelector.currentVariant || !this.foxsell || !this.categoryId) return;
    if (this.categoryId === '__add_ons__') {
      // @ts-ignore
      this.foxsell.removeFromAddOns(this.variantSelector.currentVariant, 1);
    } else {
      this.foxsell.removeFromBundle(this.variantSelector.currentVariant, 1, this.categoryId);
    }
  }

  /**
   * @description Toggle the add to bundle button
   * @param {boolean} disable
   * @returns {void}
   */
  toggleAddToBundleButton(disable) {
    this.querySelectorAll('.add-to-bundle').forEach(btn => {
      btn.toggleAttribute('disabled', disable || this.disableAddToBundle);
    });
  }
}

// Foxsell Product Options
class FoxSellVariantRadio extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellProductCard | null} */
    this.productCard = null;
    this.boundOnVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.productCard = this.closest('foxsell-product-card');
    this.onVariantChange();
    this.addEventListener('change', this.boundOnVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.boundOnVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateOptionLabels();
    this.updateVariantStatuses();
    // Dispatch variant change event to the product card
    this.dispatchEvent(new CustomEvent('variant-change', { bubbles: true }));
  }

  updateOptions() {
    this.options = [...this.querySelectorAll('fieldset')].map(fs =>
      fs.querySelector('input:checked')?.getAttribute('value') ?? ''
    );
  }

  /**
   * @description Get the FoxSell price for a variant, handling both numeric ID and GID format
   * @param {any} allowedVariants
   * @param {number} variantId
   * @returns {number}
   */
  getFoxSellPrice(allowedVariants, variantId) {
    if (!allowedVariants || !variantId) return 0;
    let foxsell_price = 0;

    if (allowedVariants[variantId]) {
      foxsell_price = allowedVariants[variantId] * 100;
    } else if (allowedVariants[`gid://shopify/ProductVariant/${variantId}`]) {
      foxsell_price = allowedVariants[`gid://shopify/ProductVariant/${variantId}`] * 100;
    }

    return foxsell_price;
  }

  /**
   * @description Get the variant data
   * @returns {FoxSellVariant[]}
   */
  getVariantData() {
    if (this.variantData) return this.variantData;
    try {
      const parsed = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '{}');

      /** @type {any[]} */
      const raw = Array.isArray(parsed.available_variants) ? parsed.available_variants : (Array.isArray(parsed) ? parsed : []);

      /** @type {FoxSellVariant[]} */
      this.variantData = raw.map(v => ({
        ...v,
        product: parsed.product ?? null,
        foxsell_price: this.getFoxSellPrice(parsed.allowed_variants, v.id)
      }));

      /** @type {FoxSellProduct | null} */
      this.product = parsed.product ?? null;
    } catch (e) {
      console.error('Failed to parse variant data:', e);
      /** @type {FoxSellVariant[]} */
      this.variantData = [];
    }
    return this.variantData ?? [];
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find(v =>
      v.options?.every((opt, i) => opt === this.options?.[i])
    );
  }

  updateOptionLabels() {
    this.querySelectorAll('fieldset').forEach((fieldset, i) => {
      const span = fieldset.querySelector('.foxsell-variant-radio__option-value, .foxsell-variant-select__option-value');
      if (span) span.textContent = this.options?.[i] ?? '';
    });
  }

  updateVariantStatuses() {
    const firstChecked = this.querySelector(':checked');
    if (!firstChecked) return;

    if (!this.variantData) return;
    const selectedOptionOneVariants = this.variantData.filter(variant => firstChecked.getAttribute('value') === variant.option1);
    const inputWrappers = [...this.querySelectorAll('fieldset')];

    inputWrappers.forEach((option, index) => {
      if (index === 0) return;

      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousFieldset = inputWrappers[index - 1];
      const previousChecked = previousFieldset?.querySelector(':checked');

      if (!previousChecked) return;

      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(variant => variant.available && variant[`option${index}`] === previousChecked.getAttribute('value'))
        .map(variantOption => variantOption[`option${index + 1}`] ?? '')
        .filter(v => v !== '');

      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  /**
   * @description Set the input availability
   * @param {Element[]} listOfOptions
   * @param {string[]} listOfAvailableOptions
   * @returns {void}
   */
  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach(input => {
      input.toggleAttribute('disabled', !listOfAvailableOptions.includes(input.getAttribute('value') ?? ''));
    });
  }
}

class FoxSellVariantSelect extends FoxSellVariantRadio {
  constructor() {
    super();
    this.boundDocumentClick = this.onDocumentClick.bind(this);
    this.boundSwatchClick = this.onSwatchClick.bind(this);
    this.boundEscape = this.onEscape.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.initSwatchDropdowns();
    document.addEventListener('click', this.boundDocumentClick);
    document.addEventListener('keydown', this.boundEscape);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundDocumentClick);
    document.removeEventListener('keydown', this.boundEscape);
    super.disconnectedCallback();
  }

  onVariantChange() {
    super.onVariantChange();
    this.syncSwatchDropdownUI();
  }

  initSwatchDropdowns() {
    if (this.dataset.swatchDropdownsInit) return;
    this.dataset.swatchDropdownsInit = 'true';
    this.addEventListener('click', this.boundSwatchClick);
    this.syncSwatchDropdownUI();
  }

  /**
   * @param {MouseEvent} e
   */
  onSwatchClick(e) {
    const t = e.target;
    const el = t instanceof Element ? t : t.parentElement;
    if (!el) return;
    const trigger = el.closest('.foxsell-swatch-dropdown__trigger');
    const optionBtn = el.closest('.foxsell-swatch-dropdown__option');
    if (trigger) {
      e.preventDefault();
      this.toggleSwatchDropdown(trigger.closest('.foxsell-swatch-dropdown'));
      return;
    }
    if (optionBtn && !optionBtn.disabled) {
      e.preventDefault();
      this.pickSwatchOption(optionBtn);
    }
  }

  /**
   * @param {MouseEvent} e
   */
  onDocumentClick(e) {
    if (!this.contains(e.target)) {
      this.closeSwatchDropdowns();
      return;
    }
    const open = this.querySelector('.foxsell-swatch-dropdown.is-open');
    if (!open) return;
    if (open.contains(e.target)) return;
    this.closeSwatchDropdowns();
  }

  /**
   * @param {KeyboardEvent} e
   */
  onEscape(e) {
    if (e.key !== 'Escape') return;
    if (!this.contains(document.activeElement)) return;
    this.closeSwatchDropdowns();
  }

  /**
   * @param {Element | null} root
   */
  toggleSwatchDropdown(root) {
    if (!root) return;
    const willOpen = !root.classList.contains('is-open');
    if (willOpen) this.closeSwatchDropdowns();
    if (willOpen) {
      root.classList.add('is-open');
      const panel = root.querySelector('.foxsell-swatch-dropdown__panel');
      const trigger = root.querySelector('.foxsell-swatch-dropdown__trigger');
      if (panel) panel.hidden = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    } else {
      this.closeSingleSwatchDropdown(root);
    }
  }

  /**
   * @param {Element} root
   */
  closeSingleSwatchDropdown(root) {
    root.classList.remove('is-open');
    const panel = root.querySelector('.foxsell-swatch-dropdown__panel');
    const trigger = root.querySelector('.foxsell-swatch-dropdown__trigger');
    if (panel) panel.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  closeSwatchDropdowns() {
    this.querySelectorAll('.foxsell-swatch-dropdown.is-open').forEach(r => this.closeSingleSwatchDropdown(r));
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  pickSwatchOption(btn) {
    const root = btn.closest('.foxsell-swatch-dropdown');
    const fieldset = root?.closest('.foxsell-swatch-dropdown-fieldset');
    const select = fieldset?.querySelector('select.foxsell-swatch-dropdown__native');
    if (!select) return;
    const value = btn.getAttribute('data-value');
    if (value == null) return;
    if (select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.syncSwatchDropdownUI();
    }
    this.closeSwatchDropdowns();
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {Element} dropdownRoot
   */
  applyTriggerFromOption(select, dropdownRoot) {
    const opt = select.selectedOptions[0];
    const trigger = dropdownRoot.querySelector('.foxsell-swatch-dropdown__trigger');
    const swatchEl = dropdownRoot.querySelector('.foxsell-swatch-dropdown__swatch');
    const label = dropdownRoot.querySelector('.foxsell-variant-select__option-value');
    if (!trigger || !swatchEl || !label) return;
    const labelText = opt ? opt.textContent.replace(/\s*-\s*unavailable\s*$/i, '').trim() : '';
    label.textContent = labelText;
    swatchEl.className = 'foxsell-swatch-dropdown__swatch';
    swatchEl.removeAttribute('style');
    if (!opt) return;
    const img = opt.getAttribute('data-swatch-image');
    const color = opt.getAttribute('data-swatch-color');
    if (img) {
      swatchEl.classList.add('foxsell-swatch-dropdown__swatch--image');
      swatchEl.style.backgroundImage = `url("${img.replace(/"/g, '\\"')}")`;
    } else if (color) {
      swatchEl.style.backgroundColor = color;
    }
  }

  syncSwatchDropdownUI() {
    this.querySelectorAll('.foxsell-swatch-dropdown-fieldset').forEach(fieldset => {
      const select = fieldset.querySelector('select.foxsell-swatch-dropdown__native');
      const dropdown = fieldset.querySelector('.foxsell-swatch-dropdown');
      if (!(select instanceof HTMLSelectElement) || !dropdown) return;
      this.applyTriggerFromOption(select, dropdown);
      const selectedVal = select.value;
      dropdown.querySelectorAll('.foxsell-swatch-dropdown__option').forEach(btn => {
        const v = btn.getAttribute('data-value');
        const opt = v != null ? [...select.options].find(o => o.value === v) : undefined;
        btn.disabled = opt ? opt.disabled : true;
        const sel = v === selectedVal;
        btn.classList.toggle('is-selected', sel);
        btn.setAttribute('aria-selected', sel ? 'true' : 'false');
      });
    });
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), select => select.value);
  }
}

// @ts-check

class FoxSellBundleSummary extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');
    this.boundUpdateBundleSummary = this.updateBundleSummary.bind(this);
    this.boundTogglePanel = this.handleTogglePanel.bind(this);
  }

  connectedCallback() {
    if (this.foxsell) {
      this.foxsell.addEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundUpdateBundleSummary);
    }

    this.toggleButton = this.querySelector('#foxsell-bundle-summary-toggle');
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', this.boundTogglePanel);
    }
  }

  disconnectedCallback() {
    if (this.foxsell) {
      this.foxsell.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundUpdateBundleSummary);
    }
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this.boundTogglePanel);
    }
  }

  /**
   * @description Toggle collapsible items panel (mobile disclosure)
   * @returns {void}
   */
  handleTogglePanel() {
    const btn = this.toggleButton;
    if (!btn) return;
    const expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  }

  /**
   * @description Update the bundle summary
   * @param {FoxSellCustomEvent} event
   * @returns {void}
   */
  updateBundleSummary(event) {
    const { bundle } = event.detail;
    const bundleItemsContainer = this.querySelector('.foxsell-bundle-summary__items-list');
    if (!bundle || !bundleItemsContainer) return

    if (!this.emptyStateHTML) {
      this.emptyStateHTML = bundleItemsContainer.innerHTML;
    }

    const items = bundle.items.flatMap((category) =>
      (Array.isArray(category.items) ? category.items : []).map(item => ({
        ...item,
        category: {
          id: category.id,
          title: category.title,
          quantity: category.quantity,
          maxQuantity: category.maxQuantity
        }
      }))
    );

    const addOnItems = bundle.addOns.items.map(item => ({
      ...item,
      category: {
        id: '__add_ons__',
        title: 'AddOns',
        quantity: item.quantity,
        maxQuantity: 1
      }
    }));

    const allItems = [...items, ...addOnItems];

    if (!allItems || allItems.length === 0) {
      bundleItemsContainer.innerHTML = this.emptyStateHTML;
      return;
    }

    bundleItemsContainer.innerHTML = allItems.map(item => {
      return this.renderLineItem(item);
    }).join('');
  }

  //! DO NOT MODIFY THIS METHOD, extend the class to override this method
  /**
   * @description Render the line item
   * @param {FoxSellLineItem} item
   * @returns {string}
   */
  renderLineItem(item) {
    let itemImage = item.featured_image ? item.featured_image.src : item.product.featured_image?.src;
    if (itemImage) {
      const separator = itemImage.includes('?') ? '&' : '?';
      itemImage = itemImage + separator + 'width=150';
    }
    let itemPrice = item.foxsell_price;
    let discountedPrice = item.foxsell_price;
    let discount = 0;
    if (!this.foxsell || !this.foxsell.config || !this.foxsell.bundle) return '';
    const priceStrategy = this.foxsell.bundle.priceStrategy;

    //! Only apply price strategy to items, not add-ons
    if (item.category.id !== '__add_ons__') {
      if (priceStrategy && priceStrategy.strategy === 'dynamic_pricing') {
        discount = priceStrategy.value;
        discountedPrice = item.foxsell_price - (item.foxsell_price * (discount / 100));
      }
    }

    const addOnStrategy = this.foxsell.config.additionalSettings.add_on_settings.strategy;

    return (`
      <foxsell-bundle-line-item data-item-id="${item.id}" data-category-id="${item.category.id}" data-category-title="${item.category.title}" data-quantity="${item.quantity}" class="foxsell-bundle-summary__item">
        <div><img src="${itemImage}"/></div>
        <div>
          ${item.category.id === '__add_ons__' ? `<span>Add-On</span>` : ''}
          <div>${item.product.title}</div>
          ${item.option1 !== 'Default Title' ? `<div>${item.options.join(", ")}</div>` : ''}
          <div>
            ${itemPrice > discountedPrice ? `
              <div>
                <span>${window.foxsell?.formatMoney?.(discountedPrice)}</span>
                <span class="foxsell-slashed-price">${window.foxsell?.formatMoney?.(itemPrice)}</span>
                <span>(${discount}% off)</span>
              </div>`
              :
              `<div>
                <span>${window.foxsell?.formatMoney?.(itemPrice)}</span>
              </div>`
            }
          </div>
        </div>
        <div class="foxsell-bundle-summary__quantity">x ${item.quantity}</div>
        ${item.category.id === '__add_ons__' && addOnStrategy === 'automatic_add' ? ""
          : `
          <div>
            <button class="foxsell-bundle-summary__item-delete" aria-label="Remove item from bundle">Delete</button>
          </div>`
        }
      </foxsell-bundle-line-item>
    `)
  }
}

class FoxSellBundleLineItem extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');
    this.boundHandleItemDelete = this.handleItemDelete.bind(this);
  }

  connectedCallback() {
    this.deleteButton = this.querySelector('button.foxsell-bundle-summary__item-delete');
    if (this.deleteButton) {
      this.deleteButton.addEventListener('click', this.boundHandleItemDelete);
    }
  }

  disconnectedCallback() {
    if (this.deleteButton) {
      this.deleteButton.removeEventListener('click', this.boundHandleItemDelete);
    }
  }

  handleItemDelete() {
    if (!this.foxsell) return;

    if (this.dataset.categoryId === '__add_ons__') {
      // @ts-ignore
      this.foxsell.removeFromAddOns({ id: parseInt(this.dataset.itemId) }, parseInt(this.dataset.quantity || '1'));
    } else {
      // @ts-ignore
      this.foxsell.removeFromBundle({ id: parseInt(this.dataset.itemId) }, parseInt(this.dataset.quantity || '1'), this.dataset.categoryId);
    }
  }
}

class FoxSellBundleProgress extends HTMLElement {
  constructor() {
    super();
    /** @type {FoxSellMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');
    this.boundHandleBundleUpdate = this.handleBundleUpdate.bind(this);
    const configEl = this.querySelector('#foxsell-bundle-progress-config');

    try {
      this.config = JSON.parse(configEl?.textContent || '{}');
    } catch (error) {
      console.error('Failed to parse foxsell bundle progress config:', error);
      this.config = {};
    }
  }

  connectedCallback() {
    this.handleBundleUpdate();

    if (this.foxsell) {
      this.foxsell.addEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdate);
    }
  }

  disconnectedCallback() {
    if (this.foxsell) {
      this.foxsell.removeEventListener(FOXSELL_EVENTS.bundleUpdated, this.boundHandleBundleUpdate);
    }
  }

  /**
   * @description Handle the bundle update
   * @returns {void}
   */
  handleBundleUpdate() {
    if (this.dataset.type === 'summary-header') {
      this.updateSummaryHeader();
    } else if (this.dataset.type === 'progress-bar') {
      this.updateProgressBar();
    }
  }

  /**
   * @description Update the summary header QAO list. Scoped to `data-type="summary-header"` in bundle-summary.liquid.
   * @returns {void}
   */
  updateSummaryHeader() {
    if (!this.foxsell?.config?.options?.length) return;

    const options = this.foxsell.config.options;
    const currentValidOption = this.foxsell.getCurrentValidOption() ?? options[0];
    const selectedId = String(currentValidOption?.variant_id ?? '');

    this.querySelectorAll('.foxsell-bundle-progress__item').forEach((el) => {
      el.classList.toggle('active', String(el.getAttribute('data-id') ?? '') === selectedId);
    });

    const priceValueEl = this.querySelector('.foxsell-bundle-progress__price-value');
    if (!priceValueEl) return;

    const { originalTotalPrice, totalPrice } = this.foxsell.bundle ?? {};
    const formatMoney = window.foxsell.formatMoney;

    if (originalTotalPrice > totalPrice) {
      priceValueEl.innerHTML = `
        <span>${formatMoney(totalPrice)}</span>
        <span class="foxsell-slashed-price">${formatMoney(originalTotalPrice)}</span>
      `;
    } else {
      priceValueEl.innerHTML = `<span>${formatMoney(totalPrice)}</span>`;
    }
  }

  /**
   * @param {FoxSellBundleProgressConfig} messageConfig
   * @returns {string}
   */
  formatMessage(messageConfig) {
    const { currentStep, maxSteps, currentQuantity, requiredQuantity, remainingQuantity, discount, isCompleted } = messageConfig;
    /** @type {string} */
    let message = '';

    if (currentStep === 0) {
      message = this.config.emptyStateText;
    } else if (isCompleted) {
      message = this.config.endStateText;
    } else {
      message = this.config.progressStateText;
    }

    message = message
    .replaceAll('_currentStep', String(currentStep))
    .replaceAll('_maxSteps', String(maxSteps))
    .replaceAll('_remainingQuantity', String(remainingQuantity))
    .replaceAll('_currentQuantity', String(currentQuantity))
    .replaceAll('_requiredQuantity', String(requiredQuantity))
    .replaceAll('_discount', String(discount));

    return message
  }

  /**
   * @description Get the bundle progress without QAO
   * @returns {FoxSellBundleProgressConfig | undefined}
   */
  getBundleProgressWithoutQAO() {
    if (!this.foxsell || !this.foxsell.config || !this.foxsell.bundle) return undefined;
    const bundle = this.foxsell.bundle.items;
    const maxSteps = this.foxsell.config.categories.reduce((acc, category) => acc + category.quantity, 0);
    const currentStep = bundle.reduce((acc, category) => acc + category.quantity, 0);
    const priceStrategy = this.foxsell.bundle.priceStrategy;
    let discount;
    if (priceStrategy?.strategy === 'dynamic_pricing') {
      discount = (priceStrategy.value ?? 0) + '%';
    } else {
      discount = window.foxsell?.formatMoney?.(parseFloat(String(priceStrategy?.value ?? 0)) * 100);
    }

    return {
      currentStep,
      maxSteps,
      discount,
      currentQuantity: currentStep,
      requiredQuantity: maxSteps,
      remainingQuantity: maxSteps - currentStep,
      isCompleted: currentStep === maxSteps,
      progress: Math.min(Math.round((currentStep / maxSteps) * 100), 100)
    };
  }


  /**
   * @description Get the bundle progress with QAO
   * @returns {FoxSellBundleProgressConfig | undefined}
   */
  getBundleProgressWithQAO() {
    if (!this.foxsell || !this.foxsell.config || !this.foxsell.bundle) return;
    const bundle = this.foxsell.bundle.items;
    const maxSteps = this.foxsell.config.options.length;
    const bundleQuantity = bundle.reduce((acc, category) => acc + category.quantity, 0);

    let eligibleOptionIndex = this.foxsell.config.options.findIndex(option => option.quantity > bundleQuantity);

    let optionIndex = eligibleOptionIndex === -1 ? maxSteps - 1 : eligibleOptionIndex;

    const option = this.foxsell.config.options[optionIndex];
    const price = option?.price;

    let discount;
    if (price?.strategy === 'dynamic_pricing') {
      discount = (price.value ?? 0) + '%';
    } else {
      discount = window.foxsell?.formatMoney?.(parseFloat(String(price?.value ?? 0)) * 100);
    }

    const currentStep = optionIndex + 1;

    const currentOption = this.foxsell.config.options[optionIndex];
    const prevOption = optionIndex > 0 ? this.foxsell.config.options[optionIndex - 1] : undefined;

    let isCompleted = (currentStep === maxSteps) && (bundleQuantity >= (currentOption?.quantity ?? 0));

    let currentQuantity = bundleQuantity;
    let requiredQuantity = currentOption?.quantity ?? 0;
    if (prevOption) {
      currentQuantity = bundleQuantity - prevOption.quantity;
      requiredQuantity = (currentOption?.quantity ?? 0) - prevOption.quantity;
    }

    let progress = Math.min(Math.round((currentQuantity / requiredQuantity) * 100), 100);

    return {
      currentStep: bundleQuantity > 0 ? currentStep : 0,
      maxSteps,
      progress,
      isCompleted,
      discount,
      currentQuantity,
      requiredQuantity,
      remainingQuantity: requiredQuantity - currentQuantity
    }
  }


  /**
   * @description Get the bundle progress
   * @returns {FoxSellBundleProgressData | undefined}
   */
  getBundleProgress() {
    if (!this.foxsell || !this.foxsell.config || !this.foxsell.bundle) return;
    const qaoEnabled = this.foxsell.bundle.qaoEnabled;
    const progressConfig = qaoEnabled ? this.getBundleProgressWithQAO() : this.getBundleProgressWithoutQAO();
    if (!progressConfig) return;
    const message = this.formatMessage(progressConfig);

    return {
      ...progressConfig,
      qaoEnabled,
      message
    };
  }

  //! DO NOT MODIFY THIS METHOD, extend the class to override this method
  /**
   * @description Update the progress bar
   * @returns {void}
   */
  updateProgressBar() {
    //! you also have access to the following properties:
    //! currentStep, maxSteps, message, progress, qaoEnabled, isCompleted, currentQuantity, requiredQuantity, remainingQuantity
    const progressData = this.getBundleProgress();
    if (!progressData) return;
    const { currentStep, maxSteps, message, progress, qaoEnabled } = progressData;
    const progressLabel = this.querySelector('.foxsell-bundle-progress__label');
    if (progressLabel) {
      progressLabel.innerHTML = message;
    }

    const progressBarsWrapper = this.querySelector('.foxsell-bundle-progress__bars');
    if (!progressBarsWrapper) return;

    progressBarsWrapper.innerHTML = '';

    for (let i = 1; i <= maxSteps; i++) {
      const progressBar = document.createElement('div');
      const progressTrack = document.createElement('div');

      progressTrack.classList.add('foxsell-bundle-progress__track');
      progressBar.classList.add('foxsell-bundle-progress__bar');

      if (i < currentStep) {
        progressBar.style.width = '100%';
      } else if (i == currentStep && qaoEnabled) {
        progressBar.style.width = `${progress}%`;
      } else if (i == currentStep && !qaoEnabled) {
        progressBar.style.width = '100%';
      }

      progressTrack.appendChild(progressBar);
      progressBarsWrapper.appendChild(progressTrack);
    }
  }
}

// @ts-check

class MellowProductCard extends FoxSellProductCard {
  constructor() {
    super();
    this.productCardInitialized = false;
  }

  /**
   * @description Get the product index
   * @returns {string}
   */
  getProductIndex() {
    return this.getAttribute('data-product-index') || '0';
  }

  /**
   * @description Add item to bundle
   * @returns {void}
   */
  addToBundle() {
    if (!this.variantSelector || !this.variantSelector.currentVariant || !this.foxsell || !this.categoryId) return;
    if (this.categoryId === '__add_ons__') {
      const quantity = parseInt(this.getAttribute('data-quantity') || '1');
      // @ts-ignore
      this.foxsell.addToAddOnsWithIndex(this.variantSelector.currentVariant, quantity, this.getProductIndex());
      if (this.isAddOnAtInventoryLimit()) {
        this.toggleAddToBundleButton(true);
      }
    } else {
      // @ts-ignore
      this.foxsell.addToBundleWithIndex(this.variantSelector.currentVariant, 1, this.categoryId, this.getProductIndex());
      if (this.isVariantAtInventoryLimit()) {
        this.toggleAddToBundleButton(true);
      }
    }
  }
  
  /**
   * @description Sync the bundle selection
   * @returns {void}
   */
  removeFromBundle() {
    if (!this.variantSelector || !this.variantSelector.currentVariant || !this.foxsell || !this.categoryId) return;
    if (this.categoryId === '__add_ons__') {
      const quantity = parseInt(this.getAttribute('data-quantity') || '1');
      // @ts-ignore
      this.foxsell.removeFromAddOnsWithIndex(this.variantSelector.currentVariant, quantity, this.getProductIndex());
    } else {
      // @ts-ignore
      this.foxsell.removeFromBundleWithIndex(this.variantSelector.currentVariant, 1, this.categoryId, this.getProductIndex());
    }
  }

  syncBundleSelection() {
    if (!this.variantSelector?.currentVariant || !this.foxsell || !this.categoryId) return;

    if (this.categoryId === '__add_ons__') {
      for (const item of this.foxsell.getSelectedAddOns()) {
        if (item.product?.id === this.productId) {
          // @ts-ignore
          this.foxsell.removeFromAddOnsWithIndex(item, item.quantity, this.getProductIndex(), false);
        }
      }
    } else {
      const category = this.foxsell.getCategory(this.categoryId);
      if (category) {
        for (const item of [...category.items.values()]) {
          if (item.product?.id === this.productId && item.properties?.__index__ === this.getProductIndex()) {
            // @ts-ignore
            this.foxsell.removeFromBundleWithIndex(item, item.quantity, this.categoryId, this.getProductIndex(), false);
          }
        }
      }
    }

    this.addToBundle();
  }

  handleVariantChange() {
    this.updateFeaturedImage();
    this.updatePrice();
    this.updateQuantity(this.getCurrentQuantity());

    if (!this.variantSelector?.currentVariant) {
      this.disableAddToBundle = true;
      this.toggleAddToBundleButton(true);
      return;
    }

    let disable;
    if (this.categoryId === '__add_ons__') {
      const bundle = this.foxsell?.bundle;
      if (!bundle) {
        disable = true;
      } else {
        const { enabled, allowedIds, isMaxedOut } = bundle.addOns;
        const isAllowed = allowedIds.includes(this.variantSelector.product?.id ?? 0);
        disable = !enabled || !isAllowed || isMaxedOut || this.isAddOnAtInventoryLimit();
      }
    } else {
      const category = this.foxsell?.getCategory(this.categoryId ?? '');
      disable = (category?.isMaxQuantity ?? false) || this.isVariantAtInventoryLimit();
    }

    this.disableAddToBundle = disable;
    this.toggleAddToBundleButton(disable);

    if (this.getAttribute('data-auto-add-to-bundle') === 'true') {
      this.syncBundleSelection();
    } else if(this.productCardInitialized) {
      this.syncBundleSelection();
    }

    this.productCardInitialized = true;
    this.updateCurrentOptionPrice();
  }

  /**
   * @description Update the current option price
   * @returns {void}
   */
  updateCurrentOptionPrice() {
    if(!this.foxsell) return;

    const currentValidOption = this.foxsell.getCurrentValidOption();
    if(!currentValidOption) return;

    const optionLabel = this.closest('label');
    const optionId = optionLabel?.getAttribute('data-option-label') || '';
    const optionPriceEle = optionLabel?.querySelector('.foxsell-product-card__option-price');
    const addOnPriceEle = optionLabel?.querySelector('.foxsell-product-card__add-on-price');

    const priceStrategy = this.foxsell.config?.settings.price.strategy;

    if(optionId !== currentValidOption.variant_id) return;
    
    if(priceStrategy === 'dynamic_pricing') {
      let itemsCompareAtPrice = 0;
      let itemsSalePrice = 0;
      itemsSalePrice = this.foxsell?.bundle.items[0]?.items.reduce(
        (sum, item) => sum + (item.foxsell_price * item.quantity),
        0
      ) ?? 0;

      itemsSalePrice = itemsSalePrice - (itemsSalePrice * (currentValidOption.price.value / 100));

      //@ts-ignore
      if(this.foxsell?.config?.useCompareAtPrice) {
        itemsCompareAtPrice = this.foxsell?.bundle.items[0]?.items.reduce(
          (sum, item) => sum + ((item.compare_at_price ?? item.price ?? 0) * item.quantity),
          0
        ) ?? 0;
      } else {
        itemsCompareAtPrice = this.foxsell?.bundle.items[0]?.items.reduce(
          (sum, item) => sum + ((item.price ?? 0) * item.quantity),
          0
        ) ?? 0;

        if(itemsSalePrice === itemsCompareAtPrice) {
          itemsCompareAtPrice = this.foxsell?.bundle.items[0]?.items.reduce(
            (sum, item) => sum + ((item.compare_at_price ?? item.price ?? 0) * item.quantity),
            0
          ) ?? 0;
        }
      }

      let addOnsCompareAtPrice = 0;
      let addOnsSalePrice = 0;
      
      addOnsSalePrice = this.foxsell?.bundle.addOns.items.reduce(
        (sum, addOn) => sum + ((addOn.foxsell_price ?? addOn.price ?? 0) * addOn.quantity),
        0
      ) ?? 0;

      //@ts-ignore
      if(this.foxsell?.config?.useCompareAtPrice) {
        addOnsCompareAtPrice = this.foxsell?.bundle.addOns.items.reduce(
          (sum, addOn) => sum + ((addOn.compare_at_price ?? addOn.price ?? 0) * addOn.quantity),
          0
        ) ?? 0;
      } else {
        addOnsCompareAtPrice = this.foxsell?.bundle.addOns.items.reduce(
          (sum, addOn) => sum + ((addOn.price ?? 0) * addOn.quantity),
          0
        ) ?? 0;

        if(addOnsSalePrice === addOnsCompareAtPrice) {
          addOnsCompareAtPrice = this.foxsell?.bundle.addOns.items.reduce(
            (sum, addOn) => sum + ((addOn.compare_at_price ?? addOn.price ?? 0) * addOn.quantity),
            0
          ) ?? 0;
        }
      }


      if(optionPriceEle) {
        if(itemsCompareAtPrice > itemsSalePrice) {
          optionPriceEle.innerHTML = `
            <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(itemsSalePrice)}</span>
            <span class="foxsell-slashed-price">${window.foxsell?.formatMoney?.(itemsCompareAtPrice)}</span>
          `;
        } else {
          optionPriceEle.innerHTML = `<span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(itemsSalePrice)}</span>`;
        }
      }

      if(addOnPriceEle) {
        if(addOnsCompareAtPrice > addOnsSalePrice) {
          addOnPriceEle.innerHTML = `
            <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(addOnsSalePrice)}</span>
            <span class="foxsell-slashed-price">${window.foxsell?.formatMoney?.(addOnsCompareAtPrice)}</span>
          `;
        } else {
          addOnPriceEle.innerHTML = `<span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(addOnsSalePrice)}</span>`;
        }
      }
    }
  }
}

class MellowBundleOptions extends HTMLElement {
  constructor() {
    super();
    /** @type {NodeListOf<HTMLInputElement>} */
    this.options = this.querySelectorAll('input[type="radio"][name="bundle-option"]');
    this.boundHandleOptionChange = this.handleOptionChange.bind(this);
    this.boundHandleAddOnCheckboxChange = this.handleAddOnCheckboxChange.bind(this);
    /** @type {MellowMixMatch | null} */
    this.foxsell = this.closest('foxsell-mix-match');
    this.addOnsCheckboxes = this.querySelectorAll('.foxsell-product-card__add-on-checkbox');
  }

  connectedCallback() {
    this.init();
    this.options.forEach(option => {
      option.addEventListener('change', this.boundHandleOptionChange);
    });

    this.addOnsCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.boundHandleAddOnCheckboxChange);
    });
  }

  disconnectedCallback() {
    this.options.forEach(option => {
      option.removeEventListener('change', this.boundHandleOptionChange);
    });

    this.addOnsCheckboxes.forEach(checkbox => {
      checkbox.removeEventListener('change', this.boundHandleAddOnCheckboxChange);
    });
  }

  /**
   * @description Initialize the bundle options
   * @returns {void}
   */
  init() {
    if(this.foxsell) {
      const firstOption = this.options[0];
      if(firstOption) {
        this.addOptionToBundle(firstOption);
      }
      this.initOptionPrice();
    }
  }

  /**
   * @description Initialize the add-ons checkboxes
   *@param {Event & { target: HTMLInputElement }} event - The event object
   * @returns {void}
   */
  handleAddOnCheckboxChange(event) {
    /** @type {MellowProductCard | null} */
    const productCard = event.target.closest('foxsell-product-card');
    if(productCard && event.target.checked) {
      productCard.addToBundle();
    } else if(productCard && !event.target.checked) {
      productCard.removeFromBundle();
    }
  }

  /**
   * @description Add an option to the bundle
   * @param {Element} option - The option to add to the bundle
   * @returns {void}
   */
  addOptionToBundle(option) {
    if(!option || !this.foxsell) return;

    // Clear the bundle and add-ons
    this.foxsell.clearBundle(false);
    this.foxsell.clearAddOns(false);

    const optionLabel = option.closest('label[data-option-label]');
    if(!optionLabel) return;

    /** @type {NodeListOf<MellowProductCard>} */
    const productCards = optionLabel.querySelectorAll('foxsell-product-card');
    productCards.forEach(productCard => {
      if(!productCard.isAddOnCard) {
        productCard.addToBundle();
      } else {
        // @ts-ignore
        const isChecked = productCard.querySelector('.foxsell-product-card__add-on-checkbox')?.checked ?? false;
        if(isChecked) {
          productCard.addToBundle();
        }
      }
    });
  }

  /**
   * @description Handle the option change event
   * @param {Event} event - The event object
   * @returns {void}
   */
  handleOptionChange(event) {
    //@ts-ignore
    const optionLabel = event.target?.closest('label[data-option-label]');
    if(!optionLabel) return;

    this.addOptionToBundle(optionLabel);
  }

  initOptionPrice() {
    if(!this.foxsell) return;

    const optionLabels = this.querySelectorAll('label[data-option-label]');
    const priceStrategy = this.foxsell.config?.settings.price.strategy;

    optionLabels.forEach(optionLabel => {

      const optionId = optionLabel.getAttribute('data-option-label') || '';
      const optionPriceEle = optionLabel.querySelector('.foxsell-product-card__option-price');
      const addOnPriceEle = optionLabel.querySelector('.foxsell-product-card__add-on-price');
      const option = this.foxsell?.config?.options.find(option => option.variant_id === optionId);

      if(!option) return;
      
      let salePrice = 0;
      let compareAtPrice = 0;

      if(priceStrategy === 'fixed_pricing'){
        const optionVariant = this.foxsell?.config?.variants.find(variant => variant.id === parseInt(optionId));
        salePrice = option.price.value * 100;
        compareAtPrice = optionVariant?.compare_at_price ?? optionVariant?.price ?? 0;
      } else {
        //@ts-ignore
        const [_itemVariantId, itemVariantPrice] = Object.entries(this.foxsell.config.categories[0].items[0].variants)[0] ?? [];
        const firstAvailableItem = this.foxsell?.config?.categoryProducts[0];
        const itemTotalPrice = itemVariantPrice * option.quantity * 100;
        salePrice = itemTotalPrice - (itemTotalPrice * (option.price.value / 100));
        compareAtPrice = itemVariantPrice * option.quantity * 100;

        if(this.foxsell?.config?.useCompareAtPrice) {
          compareAtPrice = (firstAvailableItem?.compare_at_price ?? firstAvailableItem?.price ?? 0) * option.quantity;
        } else {
          compareAtPrice = (firstAvailableItem?.price ?? 0) * option.quantity;
          if(salePrice === compareAtPrice) {
            compareAtPrice = (firstAvailableItem?.compare_at_price ?? firstAvailableItem?.price ?? 0) * option.quantity;
          }
        }
      }

      let addOnsSalePrice = 0;
      let addOnsCompareAtPrice = 0;

      //@ts-ignore
      const [_addOnProductVariantId, addOnProductVariantPrice] = Object.entries(option?.add_on_products?.[0].variants ?? {})[0] ?? [];
      const firstAvailableAddOn = this.foxsell?.config?.addOnProducts[0];

      addOnsSalePrice = addOnProductVariantPrice * option.add_on.maximum * 100;
      //@ts-ignore
      if(this.foxsell?.config?.useCompareAtPrice) {
        addOnsCompareAtPrice = (firstAvailableAddOn?.compare_at_price ?? firstAvailableAddOn?.price ?? 0) * option.add_on.maximum;
      } else {
        addOnsCompareAtPrice = (firstAvailableAddOn?.price ?? 0) * option.add_on.maximum;
        if(addOnsSalePrice === addOnsCompareAtPrice) {
          addOnsCompareAtPrice = (firstAvailableAddOn?.compare_at_price ?? firstAvailableAddOn?.price ?? 0) * option.add_on.maximum;
        }
      }

      if(optionPriceEle) {
        if(compareAtPrice > salePrice) {
          optionPriceEle.innerHTML = `
            <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(salePrice)}</span>
            <span class="foxsell-slashed-price">${window.foxsell?.formatMoney?.(compareAtPrice)}</span>
          `;
        } else {
          optionPriceEle.innerHTML = `<span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(salePrice)}</span>`;
        }
      }

      if(addOnPriceEle) {
        if(addOnsCompareAtPrice > addOnsSalePrice) {
          addOnPriceEle.innerHTML = `
            <span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(addOnsSalePrice)}</span>
            <span class="foxsell-slashed-price">${window.foxsell?.formatMoney?.(addOnsCompareAtPrice)}</span>
          `;
        } else {
          addOnPriceEle.innerHTML = `<span class="foxsell-sale-price">${window.foxsell?.formatMoney?.(addOnsSalePrice)}</span>`;
        }
      }
    });
  }
}

// JS

const elements = [
  ['foxsell-mix-match', MellowMixMatch],
  ['foxsell-category-header', FoxSellCategoryHeader],
  ['foxsell-product-card', MellowProductCard],
  ['foxsell-bundle-summary', FoxSellBundleSummary],
  ['foxsell-bundle-line-item', FoxSellBundleLineItem],
  ['foxsell-bundle-progress', FoxSellBundleProgress],
  ['foxsell-variant-radio', FoxSellVariantRadio],
  ['foxsell-variant-select', FoxSellVariantSelect],
  ['foxsell-bundle-options', MellowBundleOptions],
  ['foxsell-fixed-bundle-quantity', FixedBundleQuantity]
];


for (const [name, constructor] of elements) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
console.log("[foxsell-mellow] compiled: Fri Jun 05 2026 14:09:19 GMT+0530 (India Standard Time)");
